/*
 * Farida Gupta - re-run the Smart Wishlist app's init after the theme swaps in new content via
 * AJAX (predictive search, facets, cart drawer, quick buy...).
 *
 * The app's own script (loaded on window "load" from assets.smartwishlist.webmarked.net) scans the
 * page a single time for #smartwishlist/.smartwishlist mount points (product-card.liquid,
 * product-info.liquid) and fills them in via jQuery. Anything rendered afterwards - e.g. predictive
 * search results, which theme.js's <predictive-search> swaps in well after that initial scan - is
 * never picked up, so those mount points stay empty <div>s. The app exposes
 * window.ReloadSmartWishlist() for exactly this (it normally calls it itself after an add/remove
 * click); reuse it here on the theme's generic post-AJAX event so newly inserted mount points get
 * initialized too. Optional chaining guards the rare case this fires before the app's own
 * window-load script has attached the function.
 *
 * theme.js dispatches theme:loading:end right after its fetch resolves, but for some callers (the
 * collection/search facets handler, the quick-buy modal) the actual DOM swap - the replaceChildren()
 * call that inserts the new #smartwishlist mount points - happens synchronously AFTER that dispatch,
 * not before. Reinitializing immediately would scan the stale DOM and miss those mount points, so
 * defer one frame with requestAnimationFrame: that's after the current synchronous swap has run,
 * whichever order this particular caller happens to do it in.
 */
document.addEventListener('theme:loading:end', () => {
  requestAnimationFrame(() => window.ReloadSmartWishlist?.());
});

/*
 * Cart drawer "Pair it with" cards (snippets/fg-cart-pair-it-with.liquid, rendered via
 * snippets/product-card.liquid same as anywhere else) go through their own, separate swap instead
 * of the generic one above: theme.js's changeLineItemQuantity_fn (line-item quantity +/-, remove)
 * fires theme:loading:end right after the "cart/change.js" fetch resolves - well before the
 * cart:change event it dispatches afterwards ever reaches <cart-drawer>'s onCartChange_fn, whose
 * own async replaceContent_fn (a second, separate fetch/await) is what actually swaps in the new
 * #smartwishlist mount points. Reinitializing on theme:loading:end here would still scan the DOM
 * before that swap has happened. <cart-drawer> dispatches its own "cart-drawer:refreshed" (bubbles)
 * right after replaceContent_fn's replaceChildren() call, once the new markup genuinely exists -
 * listen for that instead for this one swap path.
 */
document.addEventListener('cart-drawer:refreshed', () => {
  requestAnimationFrame(() => window.ReloadSmartWishlist?.());
});

/*
 * The main product page renders two #smartwishlist mounts for the same product at once -
 * product-gallery.liquid's mobile-only overlay icon and product-info.liquid's title-row icon -
 * each hidden via CSS depending on viewport rather than only one existing in the DOM (same
 * id="smartwishlist"/class="smartwishlist" markup on both, per the app's expected mount pattern).
 * The app gives no real "added" event (see fg-notification-toast.js) and, going by that same file
 * needing a MutationObserver instead, only visually marks whichever single element it resolved as
 * "bookmarked" - so on a PDP the customer sees only one of the two icons fill in, even though the
 * add itself succeeded. Mirror the class across every .smartwishlist mount that shares the same
 * data-product so both icons (and any other mount for the same product elsewhere on the page, e.g.
 * a "Complete the look" card) agree. classList.toggle's force argument makes this idempotent - once
 * a mirrored mount matches, re-applying the same state is a no-op and doesn't re-trigger the
 * observer, so this can't loop.
 */
new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    const target = mutation.target;
    const productId = target.dataset?.product;

    if (!target.classList?.contains('smartwishlist') || !productId) {
      continue;
    }

    const isBookmarked = target.classList.contains('bookmarked');

    document.querySelectorAll(`.smartwishlist[data-product="${productId}"]`).forEach((mount) => {
      if (mount !== target) {
        mount.classList.toggle('bookmarked', isBookmarked);
      }
    });
  }
}).observe(document.body, {
  attributes: true,
  attributeFilter: ['class'],
  subtree: true
});
